import React, { useCallback, useMemo } from 'react';

import Autocomplete from '~/components/core/Molecules/Fields/AutoComplete';

import type { GbgAddressSuggestion, GbgRetrievedAddress } from './types';
import { useGbgAddressAutocomplete } from './useGbgAddressAutocomplete';

interface GbgAddressAutocompleteProps {
  onStreetAddress1Changed?: (streetAddress1: string) => void;
  onStreetAddress2Changed?: (streetAddress2: string) => void;
  onCityChanged?: (city: string) => void;
  onCountyChanged?: (county: string) => void;
  onCountryChanged?: (country: string) => void;
  onStateChanged?: (state: string) => void;
  onZipcodeChanged?: (zipcode: string) => void;
  onAddressValidated?: (address: GbgRetrievedAddress) => void;
  className?: string;
  id: string;
  label: string;
  disabled?: boolean;
  value?: string;
  error?: string;
  helperText?: string;
  onInputChange?: (event: React.SyntheticEvent | null, value: string) => void;
  onBlur?: () => void;
  region?: string;
}

const GbgAddressAutocomplete: React.FC<GbgAddressAutocompleteProps> = ({
  id,
  label,
  value,
  className = '',
  helperText,
  error,
  disabled,
  onBlur,
  onInputChange,
  onStreetAddress1Changed,
  onStreetAddress2Changed,
  onCityChanged,
  onCountyChanged: _onCountyChanged,
  onCountryChanged: _onCountryChanged,
  onStateChanged,
  onZipcodeChanged,
  onAddressValidated,
  region,
}) => {
  const country = region || 'AU';
  const { suggestions, isLoading, search, selectSuggestion, clearSuggestions } = useGbgAddressAutocomplete({
    country,
  });

  const suggestionsDict = useMemo(() => {
    const dict: Record<string, GbgAddressSuggestion> = {};
    for (const suggestion of suggestions) {
      dict[suggestion.id] = suggestion;
    }
    return dict;
  }, [suggestions]);

  const triggerFieldsChanged = useCallback(
    (address: GbgRetrievedAddress) => {
      if (onStreetAddress1Changed && address.street_address1) {
        onStreetAddress1Changed(address.street_address1);
      }
      if (onStreetAddress2Changed) {
        onStreetAddress2Changed(address.street_address2 || '');
      }
      if (onCityChanged && address.city) {
        onCityChanged(address.city);
      }
      if (onStateChanged && address.state) {
        onStateChanged(address.state);
      }
      if (onZipcodeChanged && address.postcode) {
        onZipcodeChanged(address.postcode);
      }
      if (onAddressValidated) {
        onAddressValidated(address);
      }
    },
    [
      onStreetAddress1Changed,
      onStreetAddress2Changed,
      onCityChanged,
      onStateChanged,
      onZipcodeChanged,
      onAddressValidated,
    ]
  );

  const handleInputChanged = (event: React.SyntheticEvent | null, inputValue: string, reason: string) => {
    if (reason === 'input') {
      if (inputValue) {
        search(inputValue);
      } else {
        clearSuggestions();
      }
    }

    if (onInputChange) {
      onInputChange(event, inputValue);
    }
  };

  const handleSuggestionSelected = async (_: React.SyntheticEvent | undefined, suggestionId?: string | null) => {
    if (!suggestionId) return;

    const address = await selectSuggestion(suggestionId);
    if (address) {
      clearSuggestions();
      triggerFieldsChanged(address);
    }
  };

  return (
    <Autocomplete
      className={className}
      id={id}
      freeSolo
      options={Object.keys(suggestionsDict)}
      getOptionLabel={(optionId: string) => suggestionsDict[optionId]?.full_address || optionId}
      onChange={handleSuggestionSelected}
      value={value || null}
      disabled={disabled}
      loading={isLoading}
      onInputChange={handleInputChanged}
      label={label}
      textFieldProps={{
        error: !!error,
        helperText: error || helperText,
        onBlur,
      }}
    />
  );
};

export default GbgAddressAutocomplete;
